import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  AddButton,
  BackButton,
  ThinDropdown,
  ThinDropdownWithSearch,
  ThinTextField,
} from "../../common/GeneralComponent";
import { fetchData } from "../../../utils/functions";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Pencil } from "lucide-react";
import Loader from "../../../components/partials/Loader";

const EditDivisions = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [divisionDetails, setDivisionDetails] = useState({
    divisionName: "",
    selectedProvinceId: null,
    Status: "",
    divisions_code: "",
  });
  const [provinces, setProvinces] = useState([]);
  const [allProvinces, setAllProvinces] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loader, setLoader] = useState(true);

  const validationSchema = Yup.object().shape({
    divisionName: Yup.string()
      .min(3, "Division name must be at least 3 characters long")
      .matches(/^[^\d]/, "Division name cannot start with a number")
      .required("Division name is required"),
    selectedProvinceId: Yup.string().required("Province selection is required"),
    Status: Yup.string().required("Status is required"),
    divisions_code: Yup.string()
      .required("Division code is required")
      .matches(/^[a-zA-Z0-9]+$/, "Division code must be alphanumeric")
      .min(2, "Division code must be at least 2 characters long")
      .max(10, "Division code must be at most 10 characters long"),
  });

  const fetchProvinces = async () => {
    try {
      const response = await fetchData(
        "provinces",
        "GET",
        null,
        {},
        {},
        navigate,
        "/provinces",
        dispatch
      );
      setLoader(false);
      const allProvs = response?.data || [];
      setAllProvinces(allProvs);

      // Filter active provinces for dropdown
      const activeProvinces = allProvs.filter((p) => p.status === 1);
      setProvinces(activeProvinces);
    } catch (error) {
      setLoader(false);
      console.error("Error fetching provinces:", error);
    }
  };

  const fetchDivisions = async () => {
    try {
      const response = await fetchData(
        "divisions",
        "GET",
        null,
        {},
        { Division_Id: id },
        navigate,
        "/divisions",
        dispatch
      );
      setLoader(false);
      const data = response?.data?.[0];
      if (data) {
        setDivisionDetails({
          divisionName: data.Name || data.name || "",
          selectedProvinceId: data.Province_Id || data.province_id || null,
          Status: data.Status || data.status,
          divisions_code: data.divisions_code || "",
        });
      }
    } catch (error) {
      setLoader(false);
      console.error("Error fetching division details:", error);
    }
  };

  useEffect(() => {
    fetchProvinces();
    fetchDivisions();
  }, [id]);

  // Function to handle status change
  const handleStatusChange = (selectedStatus) => {
    // Check if trying to activate division
    if (selectedStatus == 1 && formik.values.selectedProvinceId) {
      const selectedProvince = allProvinces.find(
        (p) => p.province_id == formik.values.selectedProvinceId
      );
      if (selectedProvince && selectedProvince.status === 0) {
        toast.error(
          "Cannot activate division. First activate related Province."
        );
        return;
      }
    }

    // Update the status if validation passes
    formik.setFieldValue("Status", selectedStatus);
  };

  const formik = useFormik({
    initialValues: divisionDetails,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoader(true);

        // Additional validation: if activating division, check if selected province is active
        if (values.Status == 1) {
          const selectedProvince = allProvinces.find(
            (p) => p.province_id == values.selectedProvinceId
          );
          if (selectedProvince && selectedProvince.status === 0) {
            toast.error(
              "Cannot activate division. First activate related Province."
            );
            setLoader(false);
            return;
          }
        }

        const response = await fetchData(
          "divisions",
          "PUT",
          {
            Division_Id: id,
            Name: values.divisionName,
            Province_Id: values.selectedProvinceId,
            Status: values.Status,
            divisions_code: values.divisions_code,
          },
          {},
          {},
          navigate,
          "/divisions",
          dispatch
        );
        toast.success(response.message);
        navigate("/divisions");
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoader(false);
      }
    },
  });

  return (
    <div className="w-full my-4 mx-auto">
      {loader && <Loader />}

      <div className="flex  lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
        <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
          Edit Division
        </h1>
        <BackButton text="Back" to="/divisions" />
      </div>
      <div className="bg-white rounded-lg shadow w-full lg:mb-[30px] mt-4  mb-[7rem]">
        <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b ">
          <h1 className="lg:text-[0.875rem] text-[13px]  tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
            Edit Division
          </h1>
        </div>
        <form className="p-4" onSubmit={formik.handleSubmit}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 p-4">
            <div>
              <ThinDropdownWithSearch
                label="Select Province"
                id="province-dropdown"
                options={provinces.map((province) => ({
                  value: province.province_id,
                  label: province.province,
                }))}
                value={formik.values.selectedProvinceId}
                onChange={(newValue) =>
                  formik.setFieldValue("selectedProvinceId", newValue || "")
                }
                onBlur={formik.handleBlur}
                error={
                  formik.touched.selectedProvinceId &&
                  formik.errors.selectedProvinceId
                }
                helperText={
                  formik.touched.selectedProvinceId &&
                  formik.errors.selectedProvinceId
                }
                required={true}
              />
              {provinces.length === 0 && (
                <p className="text-sm text-red-500 mt-1">
                  No active provinces available. Please activate a province
                  first.
                </p>
              )}
            </div>
            <ThinTextField
              id="divisions_code"
              label="Division Code"
              type="text"
              value={formik.values.divisions_code}
              onChange={() => {}}
              onBlur={() => {}}
              disabled={true}
              error={false}
              helperText="Code is auto-generated"
            />
            <ThinTextField
              id="divisionName"
              label="Division Name"
              placeholder="Hyderabad"
              type="text"
              value={formik.values.divisionName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.divisionName && formik.errors.divisionName}
              helperText={
                formik.touched.divisionName && formik.errors.divisionName
              }
            />
            <ThinDropdown
              label="Status"
              id="Status-dropdown"
              options={[
                { value: 1, label: "Active" },
                { value: 0, label: "In-Active" },
              ]}
              value={formik.values.Status}
              onChange={(e) => handleStatusChange(e.target.value)}
              onBlur={formik.handleBlur}
              error={formik.touched.Status && formik.errors.Status}
              helperText={formik.touched.Status && formik.errors.Status}
            />
          </div>
          <div className="p-4">
            <AddButton
              isLoading={isSubmitting}
              text="Submit"
              type="Submit"
              icon={Pencil}
              disabled={
                formik.isSubmitting ||
                (provinces.length === 0 && formik.values.Status == 1)
              }
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditDivisions;
